// TEAM-KPI (founder-approved 2026-10-21; spec 13 §Team KPIs and per-metric warning bands): one
// reading of a metric line's health for every surface that colours one — the scorecard grid,
// the chart lenses and panel, the DE scorecard tab, Home and the team scorecards. A line carries
// its `health` (the server's, under the metric's own bands); a line frozen before bands existed
// has none, and is read with the domain rule under its bands or the company's (100 / 70).
import {
  DEFAULT_METRIC_BANDS,
  type MetricBands,
  type MetricHealth,
  metricHealth,
} from "@companyos/domain-kpi";
import type { LensBandTone, ScoreMarkState } from "@companyos/ui";

export type { MetricBands, MetricHealth };
export { DEFAULT_METRIC_BANDS, metricHealth };

type HealthLine = {
  attainment: number | null;
  health?: MetricHealth | null | undefined;
  bands?: MetricBands | null | undefined;
};

/** A line's health: the server's, else the domain rule over its attainment and bands. */
export function lineHealth(m: HealthLine): MetricHealth | null {
  return m.health !== undefined ? m.health : metricHealth(m.attainment, m.bands);
}

/** The lens tone of a health (grey when there is none: missing is never red). */
export const healthTone = (h: MetricHealth | null): LensBandTone => h ?? "none";

/** Text colour of a line's number by its health. */
export const HEALTH_TEXT: Record<MetricHealth | "none", string> = {
  green: "text-success-text",
  amber: "text-warning-text",
  red: "text-danger-text",
  none: "text-faint",
};

/** InlineBar tone of a line's attainment bar by its health. */
export const HEALTH_BAR: Record<
  MetricHealth | "none",
  "primary" | "warning" | "danger" | "neutral"
> = {
  green: "primary",
  amber: "warning",
  red: "danger",
  none: "neutral",
};

export const HEALTH_LABEL: Record<MetricHealth | "none", string> = {
  green: "On target",
  amber: "Near target",
  red: "Off target",
  none: "No data",
};

/** The grid's mark of a health: green a hit, amber near, red a miss. */
export function healthMark(h: MetricHealth | null): ScoreMarkState {
  return h === "green" ? "hit" : h === "amber" ? "near" : h === "red" ? "miss" : "none";
}

/** "Green ≥ 100% · amber ≥ 70%" — the bands in words (the company's when absent). */
export function bandsText(b: MetricBands | null | undefined): string {
  const x = b ?? DEFAULT_METRIC_BANDS;
  return `Green ≥ ${x.green}% · amber ≥ ${x.amber}% of target`;
}
